import { z } from 'zod/v4';

export const mcpClientApplications = ['claude', 'chatgpt', 'codex'] as const;

export type McpClientApplication = (typeof mcpClientApplications)[number];

export const mcpClientPolicyReadSchema = z.strictObject({
  restricted: z.boolean(),
  applications: z.array(z.string()).transform((entries) => [...new Set(entries)]),
  clientIds: z.array(z.string()).transform((entries) => [...new Set(entries)]),
});

export const mcpClientPolicySchema = mcpClientPolicyReadSchema.extend({
  applications: z
    .array(z.enum(mcpClientApplications))
    .max(100)
    .transform((entries) => [...new Set(entries)]),
  clientIds: z
    .array(
      z
        .string()
        .min(1)
        .max(2048)
        .refine((clientId) => clientId === clientId.trim(), {
          message: 'Client IDs must not have surrounding whitespace.',
        })
        .pipe(z.url({ protocol: /^https$/ })),
    )
    .max(100)
    .transform((entries) => [...new Set(entries)]),
});

export type McpClientPolicy = z.infer<typeof mcpClientPolicySchema>;

export const defaultMcpClientPolicy: McpClientPolicy = {
  restricted: false,
  applications: [],
  clientIds: [],
};

const applicationNamespaces: readonly {
  application: McpClientApplication;
  origin: string;
  path: string;
}[] = [
  { application: 'claude', origin: 'https://claude.ai', path: '/oauth' },
  { application: 'chatgpt', origin: 'https://chatgpt.com', path: '/oauth' },
  { application: 'codex', origin: 'https://chatgpt.com', path: '/oauth/codex' },
];

export function getMcpClientApplication(clientId: string): McpClientApplication | undefined {
  let url: URL;
  try {
    url = new URL(clientId);
  } catch {
    return undefined;
  }
  // Automatic recognition must not reinterpret normalized or encoded identity paths.
  if (url.username || url.password || url.href !== clientId || url.pathname.includes('%'))
    return undefined;

  const matches = applicationNamespaces.filter(
    ({ origin, path }) =>
      url.origin === origin && (url.pathname === path || url.pathname.startsWith(`${path}/`)),
  );

  // Classify before checking a policy, so allowing ChatGPT cannot also allow Codex.
  return matches.sort((left, right) => right.path.length - left.path.length)[0]?.application;
}

export function evaluateMcpClientPolicy(policy: unknown, clientId: string) {
  const parsed = mcpClientPolicyReadSchema.safeParse(policy);
  if (!parsed.success) return { valid: false, allowed: false };
  if (!parsed.data.restricted || parsed.data.clientIds.includes(clientId)) {
    return { valid: true, allowed: true };
  }

  const application = getMcpClientApplication(clientId);
  return {
    valid: true,
    allowed: application !== undefined && parsed.data.applications.includes(application),
  };
}
